import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {useState} from 'react';

import {openSampleEntry, openSampleSequence, restoreNUEModule} from 'actions/OnboardingActions';
import BenchlingConfig from 'BenchlingConfig';
import {SECTIONS} from 'constants/NavConstants';
import * as NavConstants from 'constants/NavConstants';
import OnboardingConstants from 'constants/OnboardingConstants';
import HomeDashboardStore from 'home-dashboard/HomeDashboardStore';
import NavBarIcon from 'nav/navbar/NavBarIcon';
import * as SidepanelActions from 'nav/sidepanel/SidepanelActions';
import {
  ENTRY_CHECKLIST_KEY,
  GETTING_STARTED_CHECKLIST_KEY,
  SEQUENCE_CHECKLIST_KEY,
} from 'onboarding/OnboardingConstants';
import {trackChecklistOpened} from 'onboarding/OnboardingUtils';
import {ITEM_SIZE_SIDE_NAVBAR} from 'styles/StyleConstants';
import AbsoluteDropdownContainer from 'ui-library/AbsoluteDropdownContainer';
import {BLUE_LIGHT, RED, WHITE} from 'ui-library/Colors';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';
import SectionHeader from 'ui-library/menus/SectionHeader';

function ChecklistMenu(): JSX.Element {
  const gettingStartedModule = HomeDashboardStore.getNUEModuleState(
    OnboardingConstants.MODULES.GETTING_STARTED
  );
  const entryModule = HomeDashboardStore.getNUEModuleState(OnboardingConstants.MODULES.ENTRY);
  const sequenceModule = HomeDashboardStore.getNUEModuleState(OnboardingConstants.MODULES.SEQUENCE);

  const getStartedMenuOption = (
    <Option
      className="u-pointer"
      onSelect={() => {
        trackChecklistOpened(GETTING_STARTED_CHECKLIST_KEY, 'navbar_icon');
        SidepanelActions.openSection(NavConstants.SECTIONS.HOME);
      }}
    >
      Get started with Benchling
    </Option>
  );

  const exploreMolbioMenuOption = (
    <Option
      className="u-pointer"
      onSelect={() => {
        trackChecklistOpened(SEQUENCE_CHECKLIST_KEY, 'navbar_icon');
        restoreNUEModule(OnboardingConstants.MODULES.SEQUENCE);
        openSampleSequence();
      }}
    >
      Explore Molecular Biology
    </Option>
  );

  const exploreNotebookMenuOption = (
    <Option
      className="u-pointer"
      onSelect={() => {
        trackChecklistOpened(ENTRY_CHECKLIST_KEY, 'navbar_icon');
        restoreNUEModule(OnboardingConstants.MODULES.ENTRY);
        openSampleEntry();
      }}
    >
      Explore the Notebook
    </Option>
  );

  return (
    <Menu className={css(styles.menu)} height={Menu.HEIGHTS.MEDIUM} width={Menu.WIDTHS.LARGE}>
      <SectionHeader className={css(styles.sectionHeader)}>Checklists</SectionHeader>
      {BenchlingConfig.ENABLE_GETTING_STARTED_FLOW &&
        !gettingStartedModule?.completed &&
        getStartedMenuOption}
      {!sequenceModule?.completed && exploreMolbioMenuOption}
      {!entryModule?.completed && exploreNotebookMenuOption}
    </Menu>
  );
}

function NotificationIcon(): JSX.Element {
  return <div className={css(styles.notification)} />;
}

export default function ChecklistMenuIcon(props: {
  shouldAlignBottom: boolean;
  // Currently, we always show the notification. Eventually, we will want to be able to toggle this.
  shouldShowNotification: boolean;
}): JSX.Element {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <AbsoluteDropdownContainer
      shouldCloseOnInsideClick={true}
      menu={<ChecklistMenu />}
      onRequestClose={() => {
        setIsOpen(false);
      }}
      open={isOpen}
    >
      <div
        className={classNames(
          css(styles.item),
          css(props.shouldAlignBottom ? styles.itemAlignBottom : styles.itemAlignRelative)
        )}
      >
        <NavBarIcon
          icon="checklist"
          isActive={isOpen}
          onSectionClick={() => {
            setIsOpen(!isOpen);
          }}
          section={SECTIONS.ONBOARDING_CHECKLISTS}
        />
        {props.shouldShowNotification && <NotificationIcon />}
      </div>
    </AbsoluteDropdownContainer>
  );
}

const styles = StyleSheet.create({
  item: {
    width: ITEM_SIZE_SIDE_NAVBAR,
    height: ITEM_SIZE_SIDE_NAVBAR,
  },
  itemAlignRelative: {
    position: 'relative',
  },
  itemAlignBottom: {
    position: 'fixed',
    bottom: ITEM_SIZE_SIDE_NAVBAR,
  },
  menu: {
    margin: '0px',
    bottom: '-40px',
    left: '100%',
    position: 'absolute',
    top: 'auto',
  },
  sectionHeader: {
    backgroundColor: BLUE_LIGHT,
    marginTop: '-2px',
    borderRadius: '4px 4px 0px 0px',
    color: WHITE,
  },
  notification: {
    position: 'absolute',
    left: '16px',
    bottom: '28px',
    height: '8px',
    width: '8px',
    backgroundColor: RED,
    border: `1px solid ${WHITE}`,
    borderRadius: '50%',
  },
});
